import type { StrapiMetaType } from "@/types/strapi.type";

export type StrapiClientResponse<T> = {
  data: T | null;
  meta: StrapiMetaType["meta"] | null;
  strapiUrl?: string;
  error?: string;
};

/**
 * Client-side Strapi fetch - uses the /api/strapi proxy route.
 * Use when calling from "use client" components or client-side hooks.
 * The proxy only serves the single types in its CLIENT_CONTENT_QUERIES and sets
 * their query itself; add a new type there, otherwise it answers 404.
 *
 * @param endpoint - Strapi single type, e.g. "/cart-banner-carousel"
 */
export const fetchStrapiFromClient = async <T>(
  endpoint: string
): Promise<StrapiClientResponse<T>> => {
  const path = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  const url = `/api/strapi/${path}`;

  const response = await fetch(url, { method: "GET" });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    return {
      data: null,
      meta: null,
      strapiUrl: "",
      error:
        (error as { error?: { message?: string } })?.error?.message ??
        "Failed to fetch from Strapi",
    };
  }

  return response.json() as Promise<StrapiClientResponse<T>>;
};
